import { Component, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { ObraGlobalService } from '../../../core/navegacion/obra-global.service';
import { ObraMinima } from '../../../core/services/obra-activa.service';

/**
 * El selector de obra global (barra secundaria). Solo dibuja: lo que ofrece y lo que pasa al cambiar lo decide ObraGlobalService.
 *  - Varias obras: desplegable bien visible. Una sola: un chip fijo con su nombre (no hay nada que elegir, pero queda claro en qué obra se
 *    trabaja). Ninguna: un aviso corto.
 *  - Se maneja con el teclado: Enter o Espacio abren; flechas mueven; Enter elige; Esc cierra.
 */
@Component({
  selector: 'app-selector-obra',
  standalone: true,
  template: `
    @switch (g.modo()) {
      @case ('lista') {
        <div class="so hub-anim" [class.so--abierto]="abierto()" [class.so--ocupado]="g.cambiando()">
          <span class="so__rotulo" id="so-rotulo">Obra</span>
          <button class="so__btn" type="button" data-selector-obra aria-haspopup="listbox" [attr.aria-expanded]="abierto()"
            aria-labelledby="so-rotulo so-actual" [disabled]="g.cambiando()" (click)="alternar()" (keydown)="teclaBoton($event)">
            <span class="pi pi-building so__ico" aria-hidden="true"></span>
            @for (o of [g.actual()]; track o?.id) {
              <span class="so__nombre" id="so-actual">{{ etiqueta(o) }}</span>
            }
            @if (g.cambiando()) { <span class="pi pi-spin pi-spinner so__giro" aria-hidden="true"></span> }
            @else { <span class="pi pi-chevron-down so__flecha" aria-hidden="true"></span> }
          </button>
          @if (abierto()) {
            <ul class="so__lista" role="listbox" aria-label="Obras disponibles" tabindex="-1" data-selector-obra-lista (keydown)="teclaLista($event)">
              @for (o of g.obras(); track o.id; let i = $index) {
                <li role="option" class="so__op" [class.so__op--activa]="o.id === g.actual()?.id" [class.so__op--foco]="i === foco()"
                  [attr.aria-selected]="o.id === g.actual()?.id" [attr.data-obra-id]="o.id" (click)="elegir(o)" (mouseenter)="foco.set(i)">
                  @if (o.codigo) { <span class="so__cod">{{ o.codigo }}</span> }
                  <span class="so__op-nombre">{{ o.nombre }}</span>
                  @if (o.id === g.actual()?.id) { <span class="pi pi-check so__check" aria-hidden="true"></span> }
                </li>
              }
            </ul>
          }
        </div>
      }
      @case ('chip') {
        <div class="so so--chip hub-anim" data-selector-obra-chip title="Es la única obra donde puedes usar este módulo">
          <span class="so__rotulo">Obra</span>
          <span class="so__fija">
            <span class="pi pi-building so__ico" aria-hidden="true"></span>
            @for (o of [g.actual()]; track o?.id) { <span class="so__nombre">{{ etiqueta(o) }}</span> }
          </span>
        </div>
      }
      @case ('sin-obras') {
        <div class="so so--sin" role="status" data-selector-obra-vacio>
          <span class="pi pi-exclamation-circle" aria-hidden="true"></span>
          <span class="so__sin-txt">No tienes obras donde usar esto</span>
        </div>
      }
    }
  `,
  styleUrl: './selector-obra.scss',
})
export class SelectorObra {
  readonly g = inject(ObraGlobalService);
  private el = inject(ElementRef<HTMLElement>);

  readonly abierto = signal(false);
  readonly foco = signal(0);

  readonly indiceActual = computed(() => Math.max(0, this.g.obras().findIndex(o => o.id === this.g.actual()?.id)));

  etiqueta(o: ObraMinima | null): string {
    if (!o) return '';
    return o.codigo ? `${o.codigo} — ${o.nombre}` : o.nombre;
  }

  alternar(): void {
    if (this.abierto()) { this.abierto.set(false); return; }
    this.foco.set(this.indiceActual());
    this.abierto.set(true);
    queueMicrotask(() => (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>('.so__lista')?.focus());
  }

  async elegir(o: ObraMinima): Promise<void> {
    this.abierto.set(false);
    (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>('.so__btn')?.focus();
    if (o.id === this.g.actual()?.id) return;
    await this.g.cambiar(o.id);
  }

  teclaBoton(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); if (!this.abierto()) this.alternar(); }
  }

  teclaLista(e: KeyboardEvent): void {
    const n = this.g.obras().length;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.foco.update(i => (i + 1) % n); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.foco.update(i => (i - 1 + n) % n); }
    else if (e.key === 'Home') { e.preventDefault(); this.foco.set(0); }
    else if (e.key === 'End') { e.preventDefault(); this.foco.set(n - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); const o = this.g.obras()[this.foco()]; if (o) void this.elegir(o); }
    else if (e.key === 'Escape') { e.preventDefault(); this.abierto.set(false); (this.el.nativeElement as HTMLElement).querySelector<HTMLElement>('.so__btn')?.focus(); }
    else if (e.key === 'Tab') this.abierto.set(false);
  }

  @HostListener('document:click', ['$event'])
  fuera(e: MouseEvent): void {
    if (this.abierto() && !(this.el.nativeElement as HTMLElement).contains(e.target as Node)) this.abierto.set(false);
  }
}
